import React from 'react';
import { FaUserFriends, FaCalendarAlt, FaClock, FaPlus, FaMinus, FaChevronDown } from 'react-icons/fa';

const VisitorSelection = ({
  visitors,
  incrementVisitor,
  decrementVisitor,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  selectedOption,
  calculateTotalPrice,
  loading
}) => {
  const totalVisitors = visitors.reduce((sum, v) => sum + v.count, 0);
  const maxCapacity = selectedOption?.maxCapacity || 0;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Visitor Selection */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <FaUserFriends className="mr-2 text-blue-600" />
                Select Visitors
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                Choose number of guests for each category
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 rounded-lg">
              <div className="text-sm text-blue-700 font-medium">
                {totalVisitors} of {maxCapacity} slots filled
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {visitors.map((visitor) => (
              <div
                key={visitor.typeId}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-300 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {visitor.type}
                    </h4>
                    <div className="text-sm text-gray-500">
                      Age: {visitor.minAge}-{visitor.maxAge}
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-4">Price: {visitor.multiplier}x base rate</span>
                    {visitor.multiplier < 1 && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        Discounted
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 ml-4">
                  <button
                    onClick={() => decrementVisitor(visitor.typeId)}
                    disabled={visitor.count === 0}
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaMinus className="text-gray-600" />
                  </button>
                  <div className="w-12 text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {visitor.count}
                    </div>
                    <div className="text-xs text-gray-500">guests</div>
                  </div>
                  <button
                    onClick={() => incrementVisitor(visitor.typeId)}
                    disabled={totalVisitors >= maxCapacity}
                    className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white flex items-center justify-center hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Date & Time Selection */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 flex items-center mb-8">
            <FaCalendarAlt className="mr-2 text-blue-600" />
            Select Date & Time
          </h3>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                />
                <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Bookings available for the next 90 days
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Time Slot
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'].map((time) => (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`py-3 rounded-lg border transition-all duration-300 ${
                      selectedTime === time
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <FaClock className="text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Activity Duration</h4>
                  <p className="text-sm text-gray-600">
                    {selectedOption?.duration} minutes • Includes setup and safety briefing
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Selected Time:</span>
                <span className="font-medium text-gray-900">{selectedTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Guests:</span>
                <span className="font-bold text-lg text-gray-900">
                  {totalVisitors} {totalVisitors === 1 ? 'guest' : 'guests'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-8 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Step 3 of 4 • {totalVisitors > 0 ? `${totalVisitors} guests selected` : 'Add at least 1 guest'}
        </div>
        
        <button
          onClick={calculateTotalPrice}
          disabled={totalVisitors === 0 || !selectedDate || loading}
          className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Calculating...
            </>
          ) : (
            <>
              Proceed to Checkout
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default VisitorSelection;